import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from '@xstyled/styled-components';
import { useEditorStore } from '~/shell/Store';
import { t } from 'ttag';
import { useChatConversation } from '~/training/sequence/templates/items/chatBot/hooks/useChatConversation';
import { ChatBotService } from '~/training/sequence/templates/items/chatBot/services/chatBotService';
import { useTrainingStore } from '~/training/state';
import { LearnerTrainingStatus } from '~/data';
import { ChatHeader } from '~/training/sequence/templates/items/chatBot/components/chat/ChatHeader';
import { ChatMessageList } from '~/training/sequence/templates/items/chatBot/components/chat/ChatMessageList';
import { MessageComposer } from '~/training/sequence/templates/items/chatBot/components/chat/MessageComposer';
import { Button } from '~/ui/buttons/Button';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: calc(100dvh - 150px);
`;

const RetryButton = styled(Button)`
  align-self: center;
  margin-top: 12px;
`;

const DisclaimerText = styled.p`
  font-size: 11px;
  line-height: 1.5;
  color: #6f6f6f;
  padding: 0px 80px 18px;
  text-align: center;
  flex: 0 0 auto;

  @media (max-width: 768px) {
    font-size: 9px;
    padding: 0px 14px 18px;
  }
`;

type ChatInterfaceProps = {
  chatBotId?: string;
  shouldSendInitialMessage?: boolean;
  picture?: {
    id: string;
    name: string;
    mediaUrl: string;
  };
  title?: string;
};

export const ChatInterface = (props: ChatInterfaceProps) => {
  const editorMode = useEditorStore(state => state.editorMode);
  const learnerTrainingId = useTrainingStore(state => state.learnerTrainingId);
  const chapterSequenceId = useTrainingStore(state => state.chapterSequenceId);
  const textLocale = useTrainingStore(state => state.textLocale);
  const { chatBotId, title } = props;
  const service = useMemo(
    () =>
      editorMode
        ? null
        : new ChatBotService({
            chatBotId,
            learnerTrainingId,
            chapterSequenceId,
            textLocale,
            conversationName: title
          }),
    [
      editorMode,
      chatBotId,
      learnerTrainingId,
      chapterSequenceId,
      textLocale,
      title
    ]
  );
  const sessionKey = JSON.stringify([
    editorMode,
    chatBotId,
    learnerTrainingId,
    chapterSequenceId,
    textLocale,
    title
  ]);

  return <ChatSession key={sessionKey} {...props} chatBotService={service} />;
};

const ChatSession = ({
  chatBotService,
  shouldSendInitialMessage = false,
  picture,
  title
}: ChatInterfaceProps & { chatBotService: ChatBotService | null }) => {
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const chatContainerRef = useRef<HTMLElement | null>(null);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, []);

  const { editorMode } = useEditorStore();
  const status = useTrainingStore(state => state.status);

  const isSequenceFinished = status === LearnerTrainingStatus.Completed;

  const {
    messages,
    draft: message,
    setDraft: setMessage,
    loading,
    restored,
    initialFailed,
    sendMessage,
    sendInitialMessage
  } = useChatConversation({ chatBotService });

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (
      !editorMode &&
      !isSequenceFinished &&
      chatBotService &&
      restored &&
      shouldSendInitialMessage &&
      !hasStartedChat
    ) {
      if (!initialFailed) sendInitialMessage();
      setHasStartedChat(true);
    }
  }, [
    editorMode,
    isSequenceFinished,
    chatBotService,
    restored,
    initialFailed,
    shouldSendInitialMessage,
    sendInitialMessage,
    hasStartedChat
  ]);

  const handleSendMessage = useCallback(
    async (text: string) => {
      if (
        !text ||
        text.trim().length < 1 ||
        isSequenceFinished ||
        loading ||
        !chatBotService
      )
        return;

      setTimeout(scrollToBottom, 0);
      try {
        await sendMessage(text);
        scrollToBottom();
      } catch (e) {
        // The hook retains the draft until a reply is confirmed.
      }
    },
    [sendMessage, isSequenceFinished, scrollToBottom, loading, chatBotService]
  );

  const handleVoiceTranscript = useCallback(
    (transcript: string) => {
      if (!isSequenceFinished) setMessage(transcript);
    },
    [isSequenceFinished, setMessage]
  );

  const CHAT_BOT_TITLE = title || 'Lima Ai';

  return (
    <ChatContainer className="chatMessageRoot">
      <ChatHeader logoSrc={picture?.mediaUrl} title={CHAT_BOT_TITLE} />
      <ChatMessageList
        ref={chatContainerRef}
        messages={messages || []}
        loading={loading}
        botTitle={CHAT_BOT_TITLE}
      />
      {initialFailed && (
        <RetryButton
          type="button"
          onClick={() => sendInitialMessage()}
          disabled={loading || isSequenceFinished}
        >
          {t`Retry`}
        </RetryButton>
      )}
      <MessageComposer
        message={message}
        onMessageChange={setMessage}
        onSendMessage={handleSendMessage}
        onVoiceTranscript={handleVoiceTranscript}
        loading={loading || !restored}
        disabled={isSequenceFinished}
        placeholder={
          isSequenceFinished ? t`Chat completed` : t`Type your message...`
        }
      />
      <DisclaimerText>
        {t`This practice conversation is generated by AI to support your learning through practice. While the experience is designed to be helpful and generated dynamically, it may not cover every nuance and is not a substitute for your organization’s official policies. If you have any questions about responses, check with your employer.`}
      </DisclaimerText>
    </ChatContainer>
  );
};
